import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../services/api';
import { CreditCardIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface RediPayPaymentProps {
  amount: number;
  description: string;
  referenceNo: string;
  itemName: string;
  callbackUrl: string;
  userEmail: string;
  userName: string;
  userPhone?: string;
  caseId?: string; // REDIPAY INTEGRATION: Added caseId prop
  userId?: string; // REDIPAY INTEGRATION: Added userId prop
  onPaymentCreated?: (paymentData: {
    paymentId: string;
    paymentUrl: string;
    referenceNo: string;
  }) => void;
  onPaymentError?: (error: string) => void;
  disabled?: boolean;
}

interface RediPayResponse {
  success: boolean;
  payment: {
    id: string;
    paymentUrl: string;
    paymentId: string;
    referenceNo: string;
    amount: number;
    status: string;
    createdAt: string;
  };
  message: string;
}

const RediPayPayment: React.FC<RediPayPaymentProps> = ({
  amount,
  description,
  referenceNo,
  itemName,
  callbackUrl,
  userEmail,
  userName,
  userPhone,
  caseId, // REDIPAY INTEGRATION: Added caseId parameter
  userId, // REDIPAY INTEGRATION: Added userId parameter
  onPaymentCreated,
  onPaymentError,
  disabled = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentCreated, setPaymentCreated] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);

  const createRediPayPayment = async () => {
    if (disabled) return;

    setIsLoading(true);
    setError(null);

    try {
      // REDIPAY INTEGRATION: Use passed props instead of localStorage for better flexibility
      // For bookings without cases, we use the booking reference as case identifier
      const finalCaseId = caseId || `booking-${referenceNo}`;

      // Extract user data from JWT token as fallback
      let finalUserId = userId;
      let finalUserEmail = userEmail;
      let finalUserName = userName;
      let finalUserPhone = userPhone;

      if (!finalUserId || !finalUserEmail || !finalUserName) {
        // Try to get from localStorage token as fallback
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            finalUserId = finalUserId || tokenPayload.sub || tokenPayload.userId;
            finalUserEmail = finalUserEmail || tokenPayload.email;
            finalUserName = finalUserName || tokenPayload.name || tokenPayload.fullName;
            finalUserPhone = finalUserPhone || tokenPayload.phone;
          } catch (e) {
            console.warn('Could not parse user data from token');
          }
        }
      }

      if (!finalUserId) {
        throw new Error('User ID not found - please ensure you are logged in');
      }

      // Ensure required fields have fallbacks
      finalUserEmail = finalUserEmail || 'user@test.com';
      finalUserName = finalUserName || 'Test User';
      // MY fallback only applies when no real phone exists; strip the "+" so the
      // gateway receives digits with the country code (+6581234567 → 6581234567)
      finalUserPhone = (finalUserPhone || '0123456789').replace(/^\+/, '');

      const result: RediPayResponse = await apiRequest.post('/payments/redipay', {
        caseId: finalCaseId, // REDIPAY INTEGRATION: Use finalCaseId (booking reference for bookings without cases)
        amount,
        description,
        referenceNo,
        itemName,
        callbackUrl,
        userEmail: finalUserEmail, // REDIPAY FIX: Use finalized email with JWT fallback
        userName: finalUserName, // REDIPAY FIX: Use finalized name with JWT fallback
        userPhone: finalUserPhone, // REDIPAY FIX: Use finalized phone with JWT fallback
      });

      if (result.data.success) {
        setPaymentData(result.data.payment);
        setPaymentCreated(true);
        onPaymentCreated?.(result.data.payment);

        // Redirect to RediPay payment page
        window.location.href = result.data.payment.paymentUrl;
      } else {
        throw new Error(result.data.message || 'Payment creation failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      onPaymentError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Payment Summary */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex-shrink-0">
            <CreditCardIcon className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              AiAhad Payment
            </h3>
            <p className="text-sm text-gray-600">
              Secure instant online payment
            </p>
          </div>
        </div>

        <div className="border-t border-green-200 pt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Payment Amount:</span>
            <span className="text-lg font-bold text-gray-900">
              {formatAmount(amount)}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Reference No:</span>
            <span className="text-sm text-gray-900 font-mono">
              {referenceNo}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">Payment For:</span>
            <span className="text-sm text-gray-900 max-w-xs truncate">
              {itemName}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
          <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-gray-900">Instant Approval</h4>
            <p className="text-xs text-gray-600">Book immediately after payment</p>
          </div>
        </div>

        <div className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
          <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-gray-900">Secure Payment</h4>
            <p className="text-xs text-gray-600">SSL encrypted transactions</p>
          </div>
        </div>

        <div className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
          <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-gray-900">Multiple Options</h4>
            <p className="text-xs text-gray-600">Credit card, FPX, e-wallets</p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Payment Error</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Display */}
      {paymentCreated && paymentData && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
            <div>
              <h4 className="text-sm font-medium text-green-800">Payment Created</h4>
              <p className="text-sm text-green-700 mt-1">
                Redirecting you to secure payment page...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={createRediPayPayment}
        disabled={disabled || isLoading || paymentCreated}
        className={`
          w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200
          ${disabled || isLoading || paymentCreated
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-700 active:bg-green-800 shadow-lg hover:shadow-xl'
          }
        `}
      >
        {isLoading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Creating Payment...</span>
          </div>
        ) : paymentCreated ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Redirecting...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-2">
            <CreditCardIcon className="h-5 w-5" />
            <span>Pay {formatAmount(amount)} with AiAhad</span>
          </div>
        )}
      </button>

      {/* Trust Badges */}
      <div className="flex justify-center space-x-4 pt-4">
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span>SSL Secured</span>
        </div>
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span>Bank-Level Security</span>
        </div>
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span>PCIDSS Compliant</span>
        </div>
      </div>
    </div>
  );
};

export default RediPayPayment;